import React from "react";
import clsx from "clsx";

import { Context } from "../../../teacherStore";
import Dropdown from "../../../atoms/Dropdown";
import TeacherHeader from "../../../organisms/teacher/TeacherHeader";
import { API_URL } from "../../../teacherStore";
import {
  IPortfolio,
  OnePagePortfolioAssessments,
  ISimpleTeachingMaterial,
  IBreadcrumbsLink,
} from "../../../state";
import DownloadButton from "../../../atoms/DownloadButton";
import ModalConfirm from "../../../molecules/ModalConfirm";
import useDebounceCallback from "../../../hooks/useDebounceCallback";
import PortfolioPreview from "../../../organisms/teacher/PortfolioPreview";

import styles from "./DownloadAllPortfolio.module.scss";

export type Props = {
  calling: boolean;
  pdfToken: string | null;
  portfolioTeachingMaterials: ISimpleTeachingMaterial[];
  links: IBreadcrumbsLink[];
  class_id: number;
  goBack: () => void;
  onDownloadPDF: () => void;
  onClearPDFToken: () => void;
};

const INITIAL_PORTFOLIO: IPortfolio = {
  id: 0,
  portfolio_type: 0,
  teaching_material_id: 0,
  key_issue_answer: null,
  summary_issue_answers: null,
  lesson00_issue_answers: null,
  lesson01_issue_answers: null,
  lesson02_issue_answers: null,
  lesson03_issue_answers: null,
  one_page_portfolio_assessments: null,
};

const INITIAL_ONE_PAGE_PORTFOLIO: OnePagePortfolioAssessments = {
  id: 0,
  title: "",
  one_page_portfolio_assessment_issues: [
    {
      id: 0,
      title: "",
      one_page_portfolio_assessment_questions: [
        {
          id: 0,
          title: "",
          one_page_portfolio_assessment_answer: {
            id: 0,
            contents: "",
          },
        },
      ],
    },
  ],
};

export const DownloadAllPortfolio: React.FC<Props> = ({
  calling,
  pdfToken,
  portfolioTeachingMaterials,
  links,
  class_id,
  goBack,
  onDownloadPDF,
  onClearPDFToken,
}) => {
  const { state } = React.useContext(Context);
  const [
    teachingMaterial,
    setTeachingMaterial,
  ] = React.useState<ISimpleTeachingMaterial>(portfolioTeachingMaterials[0]);
  const [debounced] = useDebounceCallback(() => onDownloadPDF());

  const findOnePagePortfolioId =
    teachingMaterial?.lessons.find(
      lesson => lesson.one_page_portfolio_assessment_id !== null,
    )?.one_page_portfolio_assessment_id ?? null;

  const portfolio = React.useMemo(() => {
    const onePagePortfolio = state.one_page_portfolio_assessments.find(
      portfolio => portfolio.id === findOnePagePortfolioId,
    );

    return {
      ...INITIAL_PORTFOLIO,
      one_page_portfolio_assessments: [
        onePagePortfolio ?? INITIAL_ONE_PAGE_PORTFOLIO,
      ],
    };
  }, [findOnePagePortfolioId, state.one_page_portfolio_assessments]);

  return (
    <>
      <TeacherHeader links={links} />
      <div className={styles.downloadAllPortfolio}>
        <div className={styles.btnBack} onClick={goBack}>
          くクラス選択に戻る
        </div>
        <Dropdown
          className={clsx(styles.dropdown, styles.dropTeachingMaterial)}
          options={portfolioTeachingMaterials.map(material => ({
            value: material.id,
            label: `${material.name} ${material.title}`,
          }))}
          onChange={(value: number) =>
            setTeachingMaterial(
              portfolioTeachingMaterials.filter(
                teaching_material => teaching_material.id === value,
              )[0],
            )
          }
        />
        <DownloadButton
          className="ms-auto"
          disabled={calling || !portfolioTeachingMaterials.length}
          onClick={debounced}
        >
          ダウンロード
        </DownloadButton>
        {teachingMaterial && (
          <div className={styles.portfolioWrapper}>
            <PortfolioPreview
              teachingMaterial={teachingMaterial}
              portfolio={portfolio}
              onePagePortfolioId={findOnePagePortfolioId}
            />
          </div>
        )}
      </div>
      {pdfToken && (
        <ModalConfirm
          onClose={onClearPDFToken}
          title="ダウンロードしますか？"
          buttons={[
            {
              title: "キャンセル",
              action: onClearPDFToken,
              type: "None",
            },
            {
              title: "ダウンロード",
              action: () => {
                window.open(
                  `${API_URL}/teaching_materials/${teachingMaterial.id}/download_all_student_portfolio_pdf?class_id=${class_id}&temporary_token=${pdfToken}`,
                  "_blank",
                );
                onClearPDFToken();
              },
              type: "Success",
            },
          ]}
        />
      )}
    </>
  );
};

export default DownloadAllPortfolio;
